import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Checkbox, Spin } from 'antd';
import _ from 'lodash';
import { useIntersectionObserver, useMergeState } from '../../../utils/hooks';
import InstructionModal from '../../components/modals/instructionModal';
import NormalTable from '../../components/tables/normalTable';
import {
  deleteCustomerAccount, getColumns, getSearchBy, handleFetchCustomerAccounts, restoreCustomerAccount,
} from './helper';
import { toastrError, toastrSuccess } from '../../../utils/toastNotification';
import displayLoading from '../../../redux/actions/loading';

import './style.scss';

const DELETE_DESCRIPTION = 'This account will be deactivated and hidden from the app. '
  + 'It stays visible here, tagged "Deleted", and can be restored at any time.';
const RESTORE_DESCRIPTION = 'This account will be reactivated and the customer can log in again '
  + 'with their existing credentials.';

const CustomerAccounts = () => {
  const dispatch = useDispatch();
  const [state, setState] = useMergeState({
    isLoadingMore: false,
    users: [],
    deleteVisible: false,
    deleteTarget: undefined,
    restoreVisible: false,
    restoreTarget: undefined,
    lastElement: undefined,
    cursor: undefined,
  });

  const searchKey = useRef();
  const includeDeletedRef = useRef(false);

  const buildFilter = (extra = {}) => {
    const filter = { includeDeleted: includeDeletedRef.current };
    if (searchKey.current) {
      Object.assign(filter, { [searchKey.current.key]: searchKey.current.value });
    }
    Object.assign(filter, extra);
    return filter;
  };

  const onDeleteClick = (record) => {
    setState({ deleteVisible: true, deleteTarget: record });
  };

  const onRestoreClick = (record) => {
    setState({ restoreVisible: true, restoreTarget: record });
  };

  const columns = useMemo(() => getColumns(onDeleteClick, onRestoreClick));
  const searchBy = useMemo(() => getSearchBy());

  const fetchInitData = async (filter, limit) => {
    dispatch(displayLoading(true));
    const users = await handleFetchCustomerAccounts(filter, limit);
    setState({ users, cursor: users.length < limit ? undefined : users[users.length - 1]?.id || undefined });
    dispatch(displayLoading(false));
  };

  const onSearchOrFilter = async (keySearch) => {
    dispatch(displayLoading(true));
    searchKey.current = keySearch;
    const filter = buildFilter();
    const users = await handleFetchCustomerAccounts(filter, 20);
    setState({
      users,
      cursor: users.length < 20 ? undefined : users[users.length - 1]?.id || undefined,
    });
    dispatch(displayLoading(false));
  };

  const handleLoadMore = async () => {
    if (state.isLoadingMore || !state.cursor) {
      return;
    }
    const filter = buildFilter({ cursor: state.cursor });
    setState({ isLoadingMore: true });
    const users = await handleFetchCustomerAccounts(filter, 20);
    setState({
      users: [...state.users, ...users],
      cursor: users.length < 20 ? undefined : users[users.length - 1]?.id || undefined,
    });
    setState({ isLoadingMore: false });
  };

  const onToggleShowDeleted = async (e) => {
    includeDeletedRef.current = e.target.checked;
    dispatch(displayLoading(true));
    const users = await handleFetchCustomerAccounts(buildFilter(), 20);
    setState({
      users,
      cursor: users.length < 20 ? undefined : users[users.length - 1]?.id || undefined,
    });
    dispatch(displayLoading(false));
  };

  const toggleDeleteModal = () => {
    setState({ deleteVisible: !state.deleteVisible });
  };

  const toggleRestoreModal = () => {
    setState({ restoreVisible: !state.restoreVisible });
  };

  const onConfirmDelete = async () => {
    const deleted = await deleteCustomerAccount(state.deleteTarget.id);
    if (deleted.isSuccess) {
      toastrSuccess('Account deleted successfully');
      setState({
        users: state.users.map(user => (
          user.id === state.deleteTarget.id ? { ...user, isDeleted: true } : user
        )),
      });
    } else {
      toastrError(deleted.message);
    }
  };

  const onConfirmRestore = async () => {
    const restored = await restoreCustomerAccount(state.restoreTarget.id);
    if (restored.isSuccess) {
      toastrSuccess('Account restored successfully');
      setState({
        users: state.users.map(user => (
          user.id === state.restoreTarget.id ? { ...user, isDeleted: false } : user
        )),
      });
    } else {
      toastrError(restored.message);
    }
  };

  useEffect(() => {
    fetchInitData({}, 20);
  }, []);

  useEffect(() => {
    if (!_.isEmpty(state.users)) {
      const rowElements = document.querySelectorAll('.ant-table-row-level-0');
      setState({ lastElement: rowElements[rowElements.length - 1] });
    }
  }, [state.users]);

  useIntersectionObserver(state.lastElement, handleLoadMore, () => {}, [state.users, state.cursor]);

  const emptyText = searchKey.current
    ? 'No customer accounts found for this search.'
    : 'No customer accounts yet.';

  return (
    <div className="customer-accounts">
      <NormalTable
        className="customer-accounts-table"
        columns={columns}
        dataSource={state.users}
        isSearch
        searchBy={searchBy}
        emptyText={emptyText}
        fetchData={onSearchOrFilter}
        extra={<Checkbox onChange={onToggleShowDeleted}>Show deleted accounts</Checkbox>}
      />
      <div className="loadmore">
        {state.isLoadingMore && <Spin />}
      </div>
      <InstructionModal
        visible={state.deleteVisible}
        isWarning
        title={state.deleteTarget ? `Delete ${state.deleteTarget.email}?` : ''}
        description={DELETE_DESCRIPTION}
        onConfirm={onConfirmDelete}
        onCancel={toggleDeleteModal}
      />
      <InstructionModal
        visible={state.restoreVisible}
        title={state.restoreTarget ? `Restore ${state.restoreTarget.email}?` : ''}
        description={RESTORE_DESCRIPTION}
        onConfirm={onConfirmRestore}
        onCancel={toggleRestoreModal}
      />
    </div>
  );
};

export default CustomerAccounts;
